import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { XMarkIcon, UserPlusIcon, UserIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { employeesService } from '../../../services/accounting/employees';
import toast from 'react-hot-toast';

/**
 * AddEmployeeModal Component
 * 
 * Modal for adding new employees (non-drivers) to the payroll system
 */
const AddEmployeeModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { currentCompany, currentUser } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    department: '',
    salary: '',
    commissionEligible: false,
    commissionRate: '',
    status: 'active',
    notes: ''
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error(t('employees.name_required', 'Employee name is required'));
      return;
    }

    if (!formData.role.trim()) {
      toast.error(t('employees.role_required', 'Employee role is required'));
      return;
    }

    if (!currentCompany?.id) {
      toast.error('Company information is missing');
      return;
    }

    try {
      setLoading(true);
      console.log('🔄 Adding employee:', formData);
      
      const employeeData = {
        ...formData,
        companyId: currentCompany.id,
        salary: parseFloat(formData.salary) || 0,
        commissionRate: parseFloat(formData.commissionRate) || 0,
        createdBy: currentUser?.uid || 'system'
      };

      console.log('📋 Employee data to save:', employeeData);
      const employeeId = await employeesService.addEmployee(employeeData);
      console.log('✅ Employee added with ID:', employeeId);
      
      toast.success(t('employees.employee_added_success', 'Employee added successfully'));
      onSuccess();
      handleClose();
      
    } catch (error) {
      console.error('❌ Error adding employee:', error);
      console.error('Error details:', error.message);
      toast.error(`${t('employees.add_employee_failed', 'Failed to add employee')}: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: '',
      department: '',
      salary: '',
      commissionEligible: false,
      commissionRate: '',
      status: 'active',
      notes: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <UserPlusIcon className="w-6 h-6 mr-2 text-blue-600" />
              {t('employees.add_new_employee', 'Add New Employee')}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {t('employees.add_employee_description', 'Add a new employee to your payroll system')}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Basic Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <UserIcon className="w-5 h-5 mr-2 text-blue-600" />
              {t('employees.basic_information', 'Basic Information')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('employees.full_name', 'Full Name')} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder={t('employees.enter_full_name', 'Enter employee full name')}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('employees.email', t('common.email'))}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder={t('employees.enter_email', 'Enter email address')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('employees.phone', 'Phone Number')}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder={t('employees.enter_phone', 'Enter phone number')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('employees.role', 'Job Role')} *
                </label>
                <input
                  type="text"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder={t('employees.enter_role', 'e.g., Accountant, Manager, etc.')}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('employees.department', 'Department')}
                </label>
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="">{t('employees.select_department', 'Select Department')}</option>
                  <option value="administration">{t('employees.administration', 'Administration')}</option>
                  <option value="accounting">{t('employees.accounting', 'Accounting')}</option>
                  <option value="operations">{t('employees.operations', 'Operations')}</option>
                  <option value="logistics">{t('employees.logistics', 'Logistics')}</option>
                  <option value="sales">{t('employees.sales', 'Sales')}</option>
                  <option value="hr">{t('employees.hr', 'Human Resources')}</option>
                  <option value="it">{t('employees.it', 'Information Technology')}</option>
                  <option value="other">{t('employees.other', 'Other')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('employees.status', t('common.status'))}
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="active">{t('common.active', t('common.active'))}</option>
                  <option value="inactive">{t('common.inactive', t('common.inactive'))}</option>
                </select>
              </div>
            </div>
          </div>

          {/* Salary Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <CurrencyDollarIcon className="w-5 h-5 mr-2 text-green-600" />
              {t('employees.salary_information', 'Salary Information')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('employees.monthly_salary', 'Monthly Salary (SAR)')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="salary"
                  value={formData.salary}
                  onChange={handleInputChange}
                  onFocus={(e) => {
                    if (e.target.value === '0') {
                      e.target.value = '';
                    }
                  }}
                  className="form-input"
                  placeholder={t('employees.enter_salary', 'Enter monthly salary')}
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="commissionEligible"
                  name="commissionEligible"
                  checked={formData.commissionEligible}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <label htmlFor="commissionEligible" className="ml-2 text-sm text-gray-700">
                  {t('employees.commission_eligible', 'Commission Eligible')}
                </label>
              </div>

              {formData.commissionEligible && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('employees.commission_rate', 'Commission Rate (%)')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    name="commissionRate"
                    value={formData.commissionRate}
                    onChange={handleInputChange}
                    onFocus={(e) => {
                      if (e.target.value === '0') {
                        e.target.value = '';
                      }
                    }}
                    className="form-input"
                    placeholder={t('employees.enter_commission_rate', 'Enter commission rate')}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('employees.notes', t('common.notes'))}
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              className="form-textarea"
              placeholder={t('employees.enter_notes', 'Enter any additional notes about the employee')}
            />
          </div>
        </form>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="btn btn-secondary"
            disabled={loading}
          >
            {t('common.cancel', t('common.cancel'))}
          </button>
          <button
            onClick={handleSubmit}
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('common.adding', 'Adding...')}
              </>
            ) : (
              <>
                <UserPlusIcon className="w-4 h-4 mr-2" />
                {t('employees.add_employee', 'Add Employee')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEmployeeModal;